/**
 * CarrierInvoices — tracks carrier invoices we owe (Evri, Asendia, EPost, DHL, etc.)
 *
 * Each row is one downloaded invoice file. Shows amount, due date, payment status.
 * Dot-menu per row: Mark Paid · Mark Partial · Edit Details · Mark Void
 *
 * UI safety: payment status changes are behind a ⋯ menu, never a single-click toggle.
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { Icon } from '../components/Icon'
import { useAuth, authHeaders } from '../lib/auth'
import { fmtMoney } from '../lib/money'
import type { Currency } from '../lib/money'
import { relativeTime } from '../lib/money'
import { useTranslation } from 'react-i18next'

// ── Types ─────────────────────────────────────────────────────────────────────

interface InvoiceRow {
  id:                 number
  carrier:            string
  filename:           string
  source:             string
  invoice_type:       string | null
  import_status:      string
  downloaded_at:      string
  sender_email:       string | null
  email_subject:      string | null
  rows_inserted:      number | null
  file_size_bytes:    number | null
  invoice_number:     string | null
  invoice_date:       string | null
  due_date:           string | null
  amount_cents:       number | null
  currency:           string
  payment_status:     string
  paid_at:            string | null
  payment_reference:  string | null
}

interface ListResponse {
  total:    number
  limit:    number
  offset:   number
  rows:     InvoiceRow[]
  carriers: string[]
}

interface SummaryRow {
  carrier:        string
  payment_status: string
  count:          number
  total_cents:    number | null
  overdue_count:  number
}

interface SummaryResponse {
  rows:    SummaryRow[]
  overdue: { overdue_cents: number | null; overdue_count: number } | null
}

interface PatchBody {
  invoice_number?:    string | null
  invoice_date?:      string | null
  due_date?:          string | null
  amount_cents?:      number | null
  currency?:          string
  payment_status?:    string
  payment_reference?: string | null
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function paymentPill(status: string, dueDate: string | null) {
  const overdue = status === 'unpaid' && dueDate && new Date(dueDate) < new Date()
  switch (status) {
    case 'paid':
      return <span className="pill pill-mint text-[10px]">PAID</span>
    case 'partial':
      return <span className="pill pill-honey text-[10px]">PARTIAL</span>
    case 'void':
      return <span className="pill pill-ink text-[10px]">VOID</span>
    default:
      return overdue
        ? <span className="pill pill-flame text-[10px]">OVERDUE</span>
        : <span className="pill pill-royal text-[10px]">UNPAID</span>
  }
}

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function fmtAmount(cents: number | null, currency: string) {
  if (cents === null || cents === undefined) return '—'
  return fmtMoney(cents, (currency || 'USD') as Currency)
}

function daysUntilDue(dueDate: string | null): string {
  if (!dueDate) return ''
  const diff = Math.ceil((new Date(dueDate).getTime() - Date.now()) / 86_400_000)
  if (diff < 0)  return ` · ${Math.abs(diff)}d overdue`
  if (diff === 0) return ' · due today'
  if (diff <= 7)  return ` · due in ${diff}d`
  return ''
}

const PAGE_SIZE = 50

// ── Edit Details Modal ────────────────────────────────────────────────────────

interface EditModalProps {
  row: InvoiceRow
  onClose: () => void
  onSave: (body: PatchBody) => void
  saving: boolean
}

function EditModal({ row, onClose, onSave, saving }: EditModalProps) {
  const { t } = useTranslation()
  const [invoiceNum, setInvoiceNum]   = useState(row.invoice_number ?? '')
  const [invoiceDate, setInvoiceDate] = useState(row.invoice_date?.slice(0, 10) ?? '')
  const [dueDate, setDueDate]         = useState(row.due_date?.slice(0, 10) ?? '')
  const [amount, setAmount]           = useState(row.amount_cents !== null ? (row.amount_cents / 100).toFixed(2) : '')
  const [currency, setCurrency]       = useState(row.currency ?? 'USD')
  const [ref, setRef]                 = useState(row.payment_reference ?? '')

  function handleSave() {
    const amountCents = amount ? Math.round(parseFloat(amount) * 100) : null
    onSave({
      invoice_number:    invoiceNum   || null,
      invoice_date:      invoiceDate  || null,
      due_date:          dueDate      || null,
      amount_cents:      amountCents,
      currency,
      payment_reference: ref          || null,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-[15px] font-semibold text-ink-800">Edit Invoice Details</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700 p-1">
            <Icon name="x" className="w-4 h-4" />
          </button>
        </div>

        <p className="text-[12px] text-ink-500 font-mono truncate">{row.filename}</p>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Invoice Number</label>
            <input
              type="text"
              value={invoiceNum}
              onChange={e => setInvoiceNum(e.target.value)}
              placeholder={t('carrier_invoices.invoice_number_placeholder')}
              className="px-2.5 py-1.5 border border-ink-200 rounded-lg text-[13px] text-ink-700"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Invoice Date</label>
            <input
              type="date"
              value={invoiceDate}
              onChange={e => setInvoiceDate(e.target.value)}
              className="px-2.5 py-1.5 border border-ink-200 rounded-lg text-[13px] text-ink-700"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              className="px-2.5 py-1.5 border border-ink-200 rounded-lg text-[13px] text-ink-700"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Amount</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="0.00"
              className="px-2.5 py-1.5 border border-ink-200 rounded-lg text-[13px] text-ink-700"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Currency</label>
            <select
              value={currency}
              onChange={e => setCurrency(e.target.value)}
              className="px-2.5 py-1.5 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-700"
            >
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
              <option value="CAD">CAD</option>
              <option value="EUR">EUR</option>
            </select>
          </div>

          <div className="col-span-2 flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Payment Reference</label>
            <input
              type="text"
              value={ref}
              onChange={e => setRef(e.target.value)}
              placeholder={t('carrier_invoices.wire_ref_placeholder')}
              className="px-2.5 py-1.5 border border-ink-200 rounded-lg text-[13px] text-ink-700"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg border border-ink-200 text-[13px] text-ink-600 hover:bg-ink-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 rounded-lg bg-royal-500 text-white text-[13px] font-medium hover:bg-royal-600 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Dot-menu ──────────────────────────────────────────────────────────────────

interface DotMenuProps {
  row:           InvoiceRow
  onEdit:        () => void
  onMarkPaid:    () => void
  onMarkPartial: () => void
  onMarkUnpaid:  () => void
  onMarkVoid:    () => void
}

function DotMenu({ row, onEdit, onMarkPaid, onMarkPartial, onMarkUnpaid, onMarkVoid }: DotMenuProps) {
  const [open,        setOpen]        = useState(false)
  const [confirmVoid, setConfirmVoid] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
        setConfirmVoid(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const s = row.payment_status

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="p-1 rounded-lg hover:bg-ink-100 text-ink-400 hover:text-ink-700"
        title="Actions"
      >
        <Icon name="more-horizontal" className="w-4 h-4" />
      </button>

      {open && (
        <div className="absolute right-0 top-8 z-30 w-48 bg-white border border-ink-200 rounded-xl shadow-lg py-1 text-[13px]">
          <button
            onClick={() => { setOpen(false); onEdit() }}
            className="w-full text-left px-3 py-2 hover:bg-ink-50 flex items-center gap-2 text-ink-700"
          >
            <Icon name="edit-2" className="w-3.5 h-3.5 text-ink-400" />
            Edit Details
          </button>

          <div className="border-t border-ink-100 my-1" />

          {s !== 'paid' && (
            <button
              onClick={() => { setOpen(false); onMarkPaid() }}
              className="w-full text-left px-3 py-2 hover:bg-mint-50 flex items-center gap-2 text-mint-700"
            >
              <Icon name="check-circle" className="w-3.5 h-3.5" />
              Mark as Paid
            </button>
          )}
          {s !== 'partial' && (
            <button
              onClick={() => { setOpen(false); onMarkPartial() }}
              className="w-full text-left px-3 py-2 hover:bg-honey-50 flex items-center gap-2 text-honey-700"
            >
              <Icon name="minus-circle" className="w-3.5 h-3.5" />
              Mark as Partial
            </button>
          )}
          {s !== 'unpaid' && (
            <button
              onClick={() => { setOpen(false); onMarkUnpaid() }}
              className="w-full text-left px-3 py-2 hover:bg-ink-50 flex items-center gap-2 text-ink-600"
            >
              <Icon name="rotate-ccw" className="w-3.5 h-3.5" />
              Mark as Unpaid
            </button>
          )}
          {s !== 'void' && !confirmVoid && (
            <button
              onClick={() => setConfirmVoid(true)}
              className="w-full text-left px-3 py-2 hover:bg-ink-50 flex items-center gap-2 text-ink-400"
            >
              <Icon name="x-circle" className="w-3.5 h-3.5" />
              Void
            </button>
          )}
          {s !== 'void' && confirmVoid && (
            <div className="px-3 py-2 space-y-1.5">
              <p className="text-[11.5px] text-ink-500">Void this invoice?</p>
              <div className="flex gap-1.5">
                <button
                  onClick={() => { setOpen(false); setConfirmVoid(false); onMarkVoid() }}
                  className="flex-1 py-1 rounded-lg bg-ink-700 text-white text-[11.5px] font-medium hover:bg-ink-900"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setConfirmVoid(false)}
                  className="flex-1 py-1 rounded-lg border border-ink-200 text-ink-600 text-[11.5px] hover:bg-ink-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Summary Cards ─────────────────────────────────────────────────────────────

function SummaryCards({ token }: { token: string | null }) {
  const { data } = useQuery<SummaryResponse>({
    queryKey: ['carrier-invoices-summary'],
    queryFn:  () => fetch('/api/carrier-invoices/summary', { headers: authHeaders(token) }).then(r => r.json()),
  })

  const overdue = data?.overdue

  // Aggregate totals
  let totalUnpaidCents  = 0
  let totalPaidCents    = 0
  let unpaidCount       = 0
  let paidCount         = 0

  for (const row of data?.rows ?? []) {
    if (row.payment_status === 'unpaid' || row.payment_status === 'partial') {
      totalUnpaidCents += row.total_cents ?? 0
      unpaidCount      += row.count
    }
    if (row.payment_status === 'paid') {
      totalPaidCents += row.total_cents ?? 0
      paidCount      += row.count
    }
  }

  const cards = [
    {
      label: 'Outstanding',
      value: fmtMoney(totalUnpaidCents, 'USD', { compact: true }),
      sub:   `${unpaidCount} invoice${unpaidCount !== 1 ? 's' : ''}`,
      color: 'text-flame-600',
    },
    {
      label: 'Overdue',
      value: fmtMoney(overdue?.overdue_cents ?? 0, 'USD', { compact: true }),
      sub:   `${overdue?.overdue_count ?? 0} past due date`,
      color: 'text-flame-700',
    },
    {
      label: 'Paid (all time)',
      value: fmtMoney(totalPaidCents, 'USD', { compact: true }),
      sub:   `${paidCount} invoice${paidCount !== 1 ? 's' : ''}`,
      color: 'text-mint-600',
    },
  ]

  return (
    <div className="grid grid-cols-3 gap-4">
      {cards.map(c => (
        <Card key={c.label} className="p-4">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider mb-1">{c.label}</div>
          <div className={`text-[22px] font-bold tabular-nums ${c.color}`}>{c.value}</div>
          <div className="text-[11px] text-ink-400 mt-0.5">{c.sub}</div>
        </Card>
      ))}
    </div>
  )
}

// ── Main View ─────────────────────────────────────────────────────────────────

export function CarrierInvoicesView() {
  const { t } = useTranslation()
  const { token }    = useAuth()
  const queryClient  = useQueryClient()

  // Filter state
  const [carrier,   setCarrier]   = useState('')
  const [payStatus, setPayStatus] = useState('')
  const [from,      setFrom]      = useState('')
  const [to,        setTo]        = useState('')
  const [overdue,   setOverdue]   = useState(false)
  const [offset,    setOffset]    = useState(0)

  // Edit modal state
  const [editRow,   setEditRow]   = useState<InvoiceRow | null>(null)

  // Chase sync
  const [syncMsg, setSyncMsg] = useState<string | null>(null)
  const syncChase = useMutation({
    mutationFn: () =>
      fetch('/api/carrier-invoices/sync-chase', {
        method:  'POST',
        headers: authHeaders(token),
      }).then(r => r.json() as Promise<{ ok: boolean; matched: number; by_invoice_number: number; by_amount: number; amounts_backfilled: number; error?: string }>),
    onSuccess: (data) => {
      if (data.error) { setSyncMsg(`Error: ${data.error}`); return }
      const parts: string[] = []
      if ((data.amounts_backfilled ?? 0) > 0)
        parts.push(`${data.amounts_backfilled} amount${data.amounts_backfilled !== 1 ? 's' : ''} populated`)
      if (data.matched > 0)
        parts.push(`${data.matched} invoice${data.matched !== 1 ? 's' : ''} marked paid` +
          (data.by_invoice_number > 0 ? ` (${data.by_invoice_number} by invoice #, ${data.by_amount} by amount)` : ''))
      setSyncMsg(parts.length > 0 ? parts.join(' · ') + '.' : 'Already up to date.')
      queryClient.invalidateQueries({ queryKey: ['carrier-invoices'] })
      queryClient.invalidateQueries({ queryKey: ['carrier-invoices-summary'] })
    },
    onError: (e: Error) => setSyncMsg(`Error: ${e.message}`),
  })

  const applyFilter = useCallback((setter: (v: string) => void) => (v: string) => {
    setter(v); setOffset(0)
  }, [])

  const qs = new URLSearchParams()
  if (carrier)   qs.set('carrier',        carrier)
  if (payStatus) qs.set('payment_status', payStatus)
  if (from)      qs.set('from',           from)
  if (to)        qs.set('to',             to)
  if (overdue)   qs.set('overdue',        '1')
  qs.set('limit',  String(PAGE_SIZE))
  qs.set('offset', String(offset))

  const { data, isLoading } = useQuery<ListResponse>({
    queryKey: ['carrier-invoices', carrier, payStatus, from, to, overdue, offset],
    queryFn:  () => fetch(`/api/carrier-invoices?${qs}`, { headers: authHeaders(token) }).then(r => r.json()),
  })

  // PATCH mutation
  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: PatchBody }) =>
      fetch(`/api/carrier-invoices/${id}`, {
        method:  'PATCH',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      }).then(r => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carrier-invoices'] })
      queryClient.invalidateQueries({ queryKey: ['carrier-invoices-summary'] })
      setEditRow(null)
    },
  })

  function doStatus(row: InvoiceRow, status: string) {
    patch.mutate({ id: row.id, body: { payment_status: status } })
  }

  const rows       = data?.rows    ?? []
  const total      = data?.total   ?? 0
  const carriers   = data?.carriers ?? []
  const totalPages = Math.ceil(total / PAGE_SIZE)
  const page       = Math.floor(offset / PAGE_SIZE) + 1
  const hasFilters = !!(carrier || payStatus || from || to || overdue)

  function clearFilters() {
    setCarrier(''); setPayStatus(''); setFrom(''); setTo(''); setOverdue(false); setOffset(0)
  }

  return (
    <div className="space-y-5">

      {/* Summary KPI strip */}
      <SummaryCards token={token} />

      {/* Filter bar */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-end">

          <div className="flex flex-col gap-1 min-w-[140px]">
            <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Carrier</label>
            <select
              value={carrier}
              onChange={e => applyFilter(setCarrier)(e.target.value)}
              className="px-2.5 py-1.5 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-700"
            >
<option value="">{t('carrier_invoices.all_carriers')}</option>
              {carriers.map(c => (
                <option key={c} value={c}>{c.toUpperCase()}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[140px]">
            <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Status</label>
            <select
              value={payStatus}
              onChange={e => applyFilter(setPayStatus)(e.target.value)}
              className="px-2.5 py-1.5 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-700"
            >
<option value="">{t('carrier_invoices.all_statuses')}</option>
<option value="unpaid">{t('carrier_invoices.status_unpaid')}</option>
<option value="partial">{t('carrier_invoices.status_partial')}</option>
<option value="paid">{t('carrier_invoices.status_paid')}</option>
<option value="void">{t('carrier_invoices.status_void')}</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">From</label>
            <input
              type="date" value={from}
              onChange={e => applyFilter(setFrom)(e.target.value)}
              className="px-2.5 py-1.5 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-700"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">To</label>
            <input
              type="date" value={to}
              onChange={e => applyFilter(setTo)(e.target.value)}
              className="px-2.5 py-1.5 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-700"
            />
          </div>

          <div className="flex flex-col gap-1 justify-end">
            <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider invisible">x</label>
            <button
              onClick={() => { setOverdue(o => !o); setOffset(0) }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[13px] ${
                overdue
                  ? 'bg-flame-50 border-flame-300 text-flame-700'
                  : 'border-ink-200 text-ink-600 hover:bg-ink-50'
              }`}
            >
              <Icon name="alert-circle" className="w-3.5 h-3.5" />
              Overdue only
            </button>
          </div>

          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-200 text-[13px] text-ink-600 hover:bg-ink-50 mt-auto"
            >
              <Icon name="x" className="w-3.5 h-3.5" />
              Clear
            </button>
          )}

          <div className="ml-auto mt-auto flex items-center gap-3">
            <span className="text-[12px] text-ink-400">
              {isLoading ? 'Loading…' : `${total.toLocaleString()} invoice${total !== 1 ? 's' : ''}`}
            </span>
            <button
              onClick={() => { setSyncMsg(null); syncChase.mutate() }}
              disabled={syncChase.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-200 text-[13px] text-ink-600 hover:bg-ink-50 disabled:opacity-50"
              title="Match unpaid invoices against Chase bank records"
            >
              <Icon name={syncChase.isPending ? 'loader' : 'refresh-cw'} className={`w-3.5 h-3.5 ${syncChase.isPending ? 'animate-spin' : ''}`} />
              {syncChase.isPending ? 'Syncing…' : 'Sync Chase'}
            </button>
          </div>
        </div>
        {syncMsg && (
          <div className={`mt-3 flex items-center gap-2 text-[12px] px-1 ${syncMsg.startsWith('Error') ? 'text-flame-600' : 'text-mint-700'}`}>
            <Icon name={syncMsg.startsWith('Error') ? 'alert-circle' : 'check-circle'} className="w-3.5 h-3.5 flex-shrink-0" />
            {syncMsg}
            <button onClick={() => setSyncMsg(null)} className="ml-auto text-ink-400 hover:text-ink-600">
              <Icon name="x" className="w-3 h-3" />
            </button>
          </div>
        )}
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-ink-50 text-ink-500 text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left font-semibold py-2.5 px-4">Invoice</th>
              <th className="text-left font-semibold py-2.5">Carrier</th>
              <th className="text-right font-semibold py-2.5 pr-4">Amount</th>
              <th className="text-left font-semibold py-2.5 pl-4 border-l border-ink-100">Due Date</th>
              <th className="text-left font-semibold py-2.5">Status</th>
              <th className="text-left font-semibold py-2.5">Paid At</th>
              <th className="text-left font-semibold py-2.5">Reference</th>
              <th className="py-2.5 pr-4 w-10" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={8} className="py-10 text-center text-ink-400">Loading…</td></tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="py-10 text-center text-ink-400">
                  No invoices match the current filters.
                </td>
              </tr>
            )}
            {rows.map(r => {
              const isOverdue = r.payment_status === 'unpaid' && r.due_date && new Date(r.due_date) < new Date()
              return (
                <tr
                  key={r.id}
                  className={`border-t border-ink-100 hover:bg-ink-50/50 ${isOverdue ? 'bg-flame-50/30' : ''}`}
                >
                  {/* Invoice identity */}
                  <td className="py-2.5 px-4 max-w-[260px]">
                    {r.invoice_number
                      ? <div className="font-mono text-[11.5px] text-ink-800 font-semibold">{r.invoice_number}</div>
                      : <div className="text-ink-300 text-[11.5px]">—</div>
                    }
                    <div className="text-[10.5px] text-ink-400 truncate mt-0.5" title={r.filename}>
                      {r.filename}
                    </div>
                    {r.invoice_date && (
                      <div className="text-[10.5px] text-ink-400">
                        {fmtDate(r.invoice_date)}
                      </div>
                    )}
                  </td>

                  {/* Carrier */}
                  <td className="py-2.5">
                    <span className="pill pill-ink">{r.carrier.toUpperCase()}</span>
                    {r.invoice_type && (
                      <div className="text-[10.5px] text-ink-400 mt-0.5">
                        {r.invoice_type.replace('_', ' ')}
                      </div>
                    )}
                  </td>

                  {/* Amount */}
                  <td className="py-2.5 pr-4 text-right tabular-nums font-semibold text-ink-800">
                    {r.amount_cents !== null
                      ? fmtAmount(r.amount_cents, r.currency)
                      : <span className="text-ink-300 font-normal">—</span>
                    }
                    {r.amount_cents !== null && r.currency !== 'USD' && (
                      <div className="text-[10px] text-ink-400 font-normal">{r.currency}</div>
                    )}
                  </td>

                  {/* Due date */}
                  <td className="py-2.5 pl-4 border-l border-ink-100">
                    {r.due_date
                      ? <span className={`text-[12px] ${isOverdue ? 'text-flame-700 font-semibold' : 'text-ink-700'}`}>
                          {fmtDate(r.due_date)}
                          <span className={`text-[10.5px] font-normal ${isOverdue ? 'text-flame-600' : 'text-ink-400'}`}>
                            {daysUntilDue(r.due_date)}
                          </span>
                        </span>
                      : <span className="text-ink-300">—</span>
                    }
                  </td>

                  {/* Payment status */}
                  <td className="py-2.5">{paymentPill(r.payment_status, r.due_date)}</td>

                  {/* Paid at */}
                  <td className="py-2.5 text-[11.5px] text-ink-500">
                    {r.paid_at ? fmtDate(r.paid_at) : <span className="text-ink-200">—</span>}
                  </td>

                  {/* Payment reference */}
                  <td className="py-2.5 max-w-[120px]">
                    {r.payment_reference
                      ? <span className="text-[11.5px] text-ink-500 font-mono truncate block" title={r.payment_reference}>
                          {r.payment_reference}
                        </span>
                      : <span className="text-ink-200">—</span>
                    }
                  </td>

                  {/* Actions */}
                  <td className="py-2.5 pr-4">
                    <DotMenu
                      row={r}
                      onEdit={() => setEditRow(r)}
                      onMarkPaid={() => doStatus(r, 'paid')}
                      onMarkPartial={() => doStatus(r, 'partial')}
                      onMarkUnpaid={() => doStatus(r, 'unpaid')}
                      onMarkVoid={() => doStatus(r, 'void')}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="px-4 py-3 border-t border-ink-100 flex items-center justify-between text-[12px] text-ink-500">
            <span>Page {page} of {totalPages} · {total.toLocaleString()} total</span>
            <div className="flex gap-2">
              <button
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                className="px-3 py-1 border border-ink-200 rounded-lg disabled:opacity-40 hover:bg-ink-50"
              >
                ← Prev
              </button>
              <button
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
                className="px-3 py-1 border border-ink-200 rounded-lg disabled:opacity-40 hover:bg-ink-50"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Edit Details Modal */}
      {editRow && (
        <EditModal
          row={editRow}
          onClose={() => setEditRow(null)}
          onSave={body => patch.mutate({ id: editRow.id, body })}
          saving={patch.isPending}
        />
      )}
    </div>
  )
}
